import { Image } from '@chakra-ui/react'
import { DateTime } from 'luxon'

import { currentEnv } from '@/env/vars.ts'
import i18n from '@/i18n'
import {
    IApproval,
    IApprovalsDateInfoObj,
    ICompliance,
    ICompliancesDateInfoObj,
    IWidgetSettings,
    TCompliancesData,
    TIntegratedSystems,
    TMyLearning,
    TWidgetStatus,
} from '@/types/tasksApprovals'
import { deepClone } from '@/utils'
import { POST } from '@/utils/fetch'

const getApprovalsDurration = (date: string) => {
    const dateInfoObj: IApprovalsDateInfoObj = {
        waitingFor: 0,
    }

    const approvalDate: DateTime = DateTime.fromISO(date)
    const awaitingFor: number = approvalDate.diffNow('days').days * -1
    dateInfoObj['waitingFor'] = awaitingFor

    return dateInfoObj
}

export const setApprovalsInfo = (approvals: IApproval[]): IApproval[] => {
    return approvals.map((item: IApproval) => {
        const obj = getApprovalsDurration(item.approval_requested_at)

        return {
            ...item,
            ...obj,
        }
    })
}

const getCompliancesDurration = (date: string) => {
    const dateInfoObj: ICompliancesDateInfoObj = {
        overdueBy: null,
        dueIn: null,
    }

    const today: DateTime = DateTime.fromISO(DateTime.now().toString())
    const endDate: DateTime = DateTime.fromISO(date)

    if (endDate.diffNow().milliseconds >= 0) {
        dateInfoObj['dueIn'] = endDate.diff(today, 'days').days
    } else {
        dateInfoObj['overdueBy'] = today.diff(endDate, 'days').days
    }

    return dateInfoObj
}

export const setCompliancesInfo = (complicances: ICompliance[]): ICompliance[] => {
    return complicances.map((item: ICompliance) => {
        const obj = getCompliancesDurration(item.due_date)

        return {
            ...item,
            ...obj,
        }
    })
}

export const getTasksForWidget = (compliances: ICompliance[], mudId: string, settings: IWidgetSettings[]) => {
    const map: {
        [key: string]: {
            id: TIntegratedSystems
            count: number
            countHigherItemDue: number
            countHigherItemOverDue: number
            due_in: number | null
            over_due: number | null
            myCompliances: TCompliancesData
            directsCompliance: TCompliancesData
            type: 'tasks'
            title: string
            url?: string
        }
    } = {}

    // merge MY_LEARNING_RO into MY_LEARNING
    const localCompliances = deepClone(compliances)

    localCompliances.map((item) => {
        if (item.system_id === 'MY_LEARNING_RO') {
            item.system_id = 'MY_LEARNING'
        }

        return item
    })

    const getMyLearningData = (data: ICompliance[]) => {
        const MANDATORY = data.filter((item) => item?.metadata?.type === 'MANDATORY').length
        const REQUIRED = data.filter((item) => item?.metadata?.type === 'REQUIRED').length
        const OPTIONAL = data.filter((item) => item?.metadata?.type === 'OPTIONAL').length
        return {
            MANDATORY,
            REQUIRED,
            OPTIONAL,
            total: MANDATORY + REQUIRED + OPTIONAL,
        }
    }

    const myC = localCompliances.filter((item) => item.mudid === mudId)
    const directC = localCompliances.filter((item) => item.mudid !== mudId)

    localCompliances.forEach((item: ICompliance) => {
        const id = item.system_id
        const _settingsInfo = settings.filter((i) => i.system === id)[0]
        if (!map[id]) {
            map[id] = {
                id: id,
                count: 1,
                countHigherItemDue: item.dueIn ? 1 : 0,
                countHigherItemOverDue: item.overdueBy ? 1 : 0,
                due_in: item.dueIn ?? null,
                over_due: item.overdueBy ?? null,
                myCompliances: myC.length
                    ? id === 'MY_LEARNING'
                        ? getMyLearningData(myC.filter((item) => item.system_id === 'MY_LEARNING'))
                        : localCompliances.filter((item) => item.system_id === id && item.mudid === mudId).length
                    : 0,
                directsCompliance: directC.length
                    ? id === 'MY_LEARNING'
                        ? getMyLearningData(directC.filter((item) => item.system_id === 'MY_LEARNING'))
                        : localCompliances.filter((item) => item.system_id === id && item.mudid !== mudId).length
                    : 0,
                type: 'tasks',
                title: _settingsInfo?.widget_title,
                url: _settingsInfo?.widget_url,
            }
        } else {
            map[id].count += 1
            const existingDueIn = Math.round(map[id].due_in ?? 0)
            const existingOverDue = Math.round(map[id].over_due ?? 0)

            if (item.overdueBy) {
                const itemOverdueBy = Math.round(item.overdueBy)
                if (!existingOverDue || itemOverdueBy > existingOverDue) {
                    map[id].countHigherItemOverDue = 1
                    map[id].over_due = item.overdueBy
                } else if (itemOverdueBy === existingOverDue) {
                    map[id].countHigherItemOverDue += 1
                }
            } else if (item.dueIn) {
                const itemDueIn = Math.round(item.dueIn)
                if (!existingDueIn || itemDueIn < existingDueIn) {
                    map[id].countHigherItemDue = 1
                    map[id].due_in = item.dueIn
                } else if (itemDueIn === existingDueIn) {
                    map[id].countHigherItemDue += 1
                }
            }
        }
    })

    return Object.values(map)
}

export const getApprovalsForWidget = (tasks: IApproval[], settings: IWidgetSettings[]) => {
    const map: {
        [key: string]: {
            id: TIntegratedSystems
            count: number
            countHigherItem: number
            days: number
            type: 'approvals'
            title: string
            url?: string
        }
    } = {}

    tasks.forEach((item) => {
        const id = item.system_id
        const _settingsInfo = settings.filter((i) => i.system === id)[0]
        if (!map[id]) {
            map[id] = {
                id: id,
                count: 1,
                countHigherItem: 1,
                days: item.waitingFor,
                type: 'approvals',
                title: _settingsInfo?.widget_title,
                url: _settingsInfo?.widget_url,
            }
        } else {
            map[id].count += 1
            const nrDays = Math.round(map[id].days)
            const waitingFor = Math.round(item.waitingFor)

            if (waitingFor && (nrDays || nrDays === 0) && waitingFor > nrDays) {
                map[id].countHigherItem = 1
                map[id].days = item.waitingFor
            } else if (waitingFor === nrDays) {
                map[id].countHigherItem += 1
            }
        }
    })

    return Object.values(map)
}

export const getDaysText = (days: number, status?: TWidgetStatus) => {
    if (days < 1 && status !== 'waiting') {
        return 'Today'
    } else if (days < 1 && status === 'waiting') {
        return '0 days'
    } else if (days >= 1 && days < 1.5) {
        return days.toFixed(0) + ' day'
    } else {
        return days.toFixed(0) + ' days'
    }
}

export const getInfoIcon = (days: number, status: TWidgetStatus, isSelected?: boolean) => {
    const stroke = isSelected ? '_stroke' : ''
    if (days < 1 && status === 'waiting') {
        return <Image src={`/assets/new_icon${stroke}.svg`} width="13px" objectFit="fill" />
    }
    if (days >= 2 && days < 5 && (status === 'duein' || status === 'waiting')) {
        return <Image src={`/assets/alert_icon${stroke}.svg`} width="13px" objectFit="fill" />
    }
    if ((days >= 5 && status === 'waiting') || (days && days < 2 && status === 'duein') || status === 'overdue') {
        return <Image src={`/assets/warning_icon${stroke}.svg`} width="13px" objectFit="fill" />
    }
    return null
}

export const getDueOverDueText = (days: number, status: TWidgetStatus, variant: 'short' | 'long' = 'long'): string => {
    let text = ''
    if (status === 'overdue')
        text = i18n.t(
            variant === 'short'
                ? 'tasksApprovals.widgets.overdue_short'
                : days < 0
                  ? 'tasksApprovals.widgets.overdue'
                  : 'tasksApprovals.widgets.overdue_by',
        )
    if (status === 'duein')
        text = i18n.t(
            variant === 'short' ? 'tasksApprovals.widgets.due' : days < 0 ? 'tasksApprovals.widgets.due' : 'tasksApprovals.widgets.due_in',
        )

    return text
}

export const trackWidgetsClick = async (systemId: TIntegratedSystems, hideError: boolean) => {
    return await POST(`${currentEnv.utilityUrl}/metric/widget-clicks`, { widget_identifier: systemId }, false, hideError)
}

export const getDataForMyLearning = (compliances: ICompliance[]) => {
    const map: {
        [key: string]: {
            id: TIntegratedSystems
            count: number
            countHigherItemDue: number
            countHigherItemOverDue: number
            due_in: number | null
            over_due: number | null
            type: 'tasks'
            bodyText: string
            icon: JSX.Element | null
        }
    } = {}

    const localCompliances = deepClone(compliances)

    localCompliances.forEach((item: ICompliance) => {
        const id = item.system_id
        if (!map[id]) {
            map[id] = {
                id: id,
                count: 1,
                countHigherItemDue: item.dueIn ? 1 : 0,
                countHigherItemOverDue: item.overdueBy ? 1 : 0,
                due_in: item.dueIn ?? null,
                over_due: item.overdueBy ?? null,
                type: 'tasks',
                bodyText: '',
                icon: null,
            }
        } else {
            map[id].count += 1
            const existingDueIn = Math.round(map[id].due_in ?? 0)
            const existingOverDue = Math.round(map[id].over_due ?? 0)

            if (item.overdueBy) {
                const itemOverdueBy = Math.round(item.overdueBy)
                if (!existingOverDue || itemOverdueBy > existingOverDue) {
                    map[id].countHigherItemOverDue = 1
                    map[id].over_due = item.overdueBy
                } else if (itemOverdueBy === existingOverDue) {
                    map[id].countHigherItemOverDue += 1
                }
            } else if (item.dueIn) {
                const itemDueIn = Math.round(item.dueIn)
                if (!existingDueIn || itemDueIn < existingDueIn) {
                    map[id].countHigherItemDue = 1
                    map[id].due_in = item.dueIn
                } else if (itemDueIn === existingDueIn) {
                    map[id].countHigherItemDue += 1
                }
            }
        }
    })

    return Object.values(map)
}

export const getMyLearningTotals = (type: TMyLearning, directsCompliances: TCompliancesData, myCompliances: TCompliancesData) => {
    let total = 0
    if (type !== 'OPTIONAL') {
        if (typeof directsCompliances === 'object' && typeof myCompliances === 'object') {
            total = directsCompliances[type] + myCompliances[type]
        } else if (typeof myCompliances === 'object' && directsCompliances === 0) {
            total = myCompliances[type]
        } else if (typeof directsCompliances === 'object' && myCompliances === 0) {
            total = directsCompliances[type]
        }
        return total
    }
    return typeof myCompliances === 'object' ? myCompliances.OPTIONAL : 0
}
